// import { QueryClient } from '@tanstack/vue-query';
import type { DataTableSortEvent } from 'primevue/datatable';
import type { LocationQuery } from 'vue-router';

type Args<T> = {
  query: (routeQuery: LocationQuery) => Promise<T>
  uniqueKey: string
  placeholder?: object
  useCache?: boolean | 'short'
  useFullPath?: boolean
};

export function useDataQuery<T>({ query, uniqueKey, useCache, useFullPath = false }: Args<T>) {
  const queryClient = useQueryClient();
  const route = useRoute();
  const router = useRouter();

  const fullPath = computed(() => {
    return route.fullPath;
  });

  const routeQuery = computed(() => {
    return route.query;
  });

  const { data, isLoading, isFetched, isPending, isFetching } = useQuery<T>({
    queryKey: [uniqueKey ?? route.name ?? route.path, useFullPath ? fullPath : ''],
    queryFn: async () => {
      const data = await query(routeQuery.value);
      return data;
    },
    staleTime: useCache ? (useCache === 'short' ? 60000 : 300000) : 0,
  });

  const invalidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: [uniqueKey ?? route.name ?? route.path],
    });
  };

  const onPaginationChage = async (page: number) => {
    router.push({
      path: route.path,
      query: {
        ...route.query,
        page,
      },
    });
  };

  const onSizeChange = async (size: number) => {
    router.push({
      path: route.path,
      query: {
        ...route.query,
        page: 1,
        page_size: size,
      },
    });
  };

  const onSortChange = (event: DataTableSortEvent) => {
    const newQueries = { ...route.query };
    if (event.sortOrder !== null) {
      newQueries.sort = `${event.sortField}:${event.sortOrder === 1 ? 'asc' : 'desc'}`;
    }
    else {
      delete newQueries.sort;
    }
    router.push({ ...route, query: newQueries });
  };

  return {
    data,
    isLoading,
    isFetched,
    queryClient,
    isPending,
    isFetching,
    invalidateQuery,
    onPaginationChage,
    onSizeChange,
    onSortChange,
  };
}

export default useDataQuery;
